/**
 * Single source of truth for the signed-in customer on a partner page.
 * Every element (login button, checkout overlays, profile) subscribes via
 * useSyncExternalStore; the token lives in localStorage so it survives page
 * loads and is shared across tabs (storage event). Mirrors basket-store.ts.
 */
export type AuthUser = { id: string; email: string; name: string };
export type AuthStatus = "unknown" | "anonymous" | "authenticated";
export type AuthSnapshot = { status: AuthStatus; token: string | null; user: AuthUser | null };

const TOKEN_KEY = "baluu-customer-token";
const AUTH_CHANGED_EVENT = "baluu:auth-changed"; // same-page broadcast between elements
export const LOGIN_EVENT = "baluu:login";        // public, on document
export const LOGOUT_EVENT = "baluu:logout";      // public, on document

function readToken(): string | null {
  try { return window.localStorage.getItem(TOKEN_KEY); } catch { return null; }
}
function writeToken(token: string | null) {
  try {
    if (token) window.localStorage.setItem(TOKEN_KEY, token);
    else window.localStorage.removeItem(TOKEN_KEY);
  } catch { /* private mode etc. — session just won't persist */ }
}

let snapshot: AuthSnapshot = (() => {
  const token = typeof window === "undefined" ? null : readToken();
  return { status: token ? "unknown" : "anonymous", token, user: null };
})();

type Listener = () => void;
const listeners = new Set<Listener>();
function notify() { listeners.forEach((fn) => fn()); }

function publish(name: string, detail: object) {
  if (typeof document === "undefined") return;
  document.dispatchEvent(new CustomEvent(name, { detail }));
}

function replace(next: AuthSnapshot) {
  const prev = snapshot;
  snapshot = next;
  notify();
  if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent(AUTH_CHANGED_EVENT));
  if (next.status === "authenticated" && prev.status !== "authenticated" && next.user) {
    publish(LOGIN_EVENT, { userId: next.user.id, email: next.user.email });
  }
  if (prev.status === "authenticated" && next.status !== "authenticated") {
    publish(LOGOUT_EVENT, { userId: prev.user?.id, email: prev.user?.email });
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key !== TOKEN_KEY) return;
    const token = e.newValue;
    replace(token ? { status: "unknown", token, user: null } : { status: "anonymous", token: null, user: null });
  });
  window.addEventListener(AUTH_CHANGED_EVENT, notify);
}

export const authStore = {
  getSnapshot: (): AuthSnapshot => snapshot,
  subscribe(fn: Listener): () => void { listeners.add(fn); return () => listeners.delete(fn); },
  getToken: (): string | null => snapshot.token,
  /** Token received (set-auth-token) but the user not yet fetched. */
  setToken(token: string) { writeToken(token); replace({ status: "unknown", token, user: null }); },
  setAuthenticated(token: string, user: AuthUser) {
    writeToken(token);
    replace({ status: "authenticated", token, user });
  },
  setAnonymous() { writeToken(null); replace({ status: "anonymous", token: null, user: null }); },
};
